import { user } from 'services/Utils';
import Compressor from 'compressorjs';

const API_BASE_URL = process.env.REACT_APP_API_SERVER;
const PETROL_API_SERVER = process.env.REACT_APP_PETROL_API_SERVER;

function createApiFetcher(baseUrl) {
  return async function fetchAPI(path, options = {}) {
    const { responseType = 'json' } = options;
    delete options.responseType;

    let queryString = '';
    if (options.params) {
      const searchParams = new URLSearchParams(options.params);
      queryString = `?${searchParams.toString()}`;
      delete options.params;
    }

    const url = `${baseUrl}${path}${queryString}`;

    const headers =
      options.body instanceof FormData
        ? options.headers
        : {
            'Content-Type': 'application/json',
            ...options.headers,
          };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      localStorage.removeItem('auth');
      window.location.href = '/#/auth/signin';
    }

    if ([400, 403, 404, 409, 422].includes(response.status)) {
      return response.json();
    }

    if (response.status === 204) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`API call failed: ${url}, status: ${response.status}`);
    }

    if (responseType === 'text') {
      return response.text();
    }

    if (responseType === 'blob') {
      return response.blob();
    }

    return response.json();
  };
}

const fetchAPI = createApiFetcher(API_BASE_URL);
const fetchPetrolAPI = createApiFetcher(PETROL_API_SERVER);

function userAuth() {
  return { Authorization: `Bearer ${user().token}` };
}

export const apiService = {
  /* General APIs */

  // auth
  async recoverPassword(data) {
    return fetchAPI('/auth/recover-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async recoverPasswordConfirm(data) {
    return fetchAPI('/auth/recover-password-confirm', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async login(data) {
    return fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async authTwoFA(data) {
    return fetchAPI('/auth/twofa', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  /**/ async refreshToken(data) {
    return fetchAPI('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async logout() {
    return fetchAPI('/auth/logout', { method: 'POST', headers: userAuth() });
  },
  // auth sessions
  async getSessions(params) {
    return fetchAPI('/auth/sessions', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },
  async deleteAllSessions() {
    return fetchAPI('/auth/sessions', {
      method: 'DELETE',
      headers: userAuth(),
    });
  },
  async deleteSession(params) {
    return fetchAPI(`/auth/sessions/${params.sessionId}`, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },
  // users and profile
  async getMe() {
    return fetchAPI('/users/me', { method: 'GET', headers: userAuth() });
  },
  async updateUser(data) {
    return fetchAPI('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async updateUserEmail(data) {
    return fetchAPI('/users/change-email', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async updateUserPassword(data) {
    return fetchAPI('/users/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async updateUserPhone(data) {
    return fetchAPI('/users/change-phone', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async deleteUser(data) {
    return fetchAPI('/users/delete', {
      method: 'DELETE',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async userTwoFAList() {
    return fetchAPI('/twofa', { method: 'GET', headers: userAuth() });
  },
  async userTwoFA(data) {
    return fetchAPI('/twofa/' + data.type, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async uploadImage(data) {
    const file = data.get('file');
    if (!file) {
      return;
    }

    try {
      const compressedFile = await new Promise((resolve, reject) => {
        new Compressor(file, {
          quality: 0.3,
          maxHeight: 2048,
          maxWidth: 2048,
          mimeType: file.type,
          success(result) {
            resolve(
              new File([result], file.name, {
                type: file.type,
                lastModified: Date.now(),
              }),
            );
          },
          error(err) {
            reject(err);
          },
        });
      });

      const compressedData = new FormData();
      for (const [key, value] of data.entries()) {
        compressedData.append(key, key === 'file' ? compressedFile : value);
      }

      return fetchAPI('/admin/upload', {
        method: 'POST',
        body: compressedData,
        headers: userAuth(),
      });
    } catch (error) {
      console.error('Image compression error:', error);
    }
  },

  async deleteImage(data) {
    return fetchAPI('/admin/upload', {
      method: 'DELETE',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async addEntry(data) {
    return fetchAPI(`/admin/giveaways/${data.id}/manual-entries`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async bulkAddEntries({ id, data }) {
    return fetchAPI(`/admin/giveaways/${id}/manual-entries/bulk`, {
      method: 'POST',
      body: data,
      headers: userAuth(),
    });
  },

  async getGiveawaysEntriesCount(params) {
    return fetchAPI(`/admin/giveaways/${params.id}/entries`, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  // giveaways
  async getGiveaways(params) {
    return fetchAPI('/admin/giveaways', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getGiveaway(params) {
    return fetchAPI('/admin/giveaways/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },
  async createGiveaway(data) {
    return fetchAPI('/admin/giveaways', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async updateGiveaway(data) {
    return fetchAPI('/admin/giveaways/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async deleteGiveaway(params) {
    return fetchAPI('/admin/giveaways/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },
  async getGiveawaysRewardsTypes(params) {
    return fetchAPI('/admin/giveaways-rewards-types', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async deleteUserSession(params) {
    return fetchAPI(
      '/admin/users/' + params.userId + '/sessions/' + params.sessionId,
      { method: 'DELETE', headers: userAuth() },
    );
  },

  async getMeRole() {
    return fetchAPI('/admin/roles/user/me', {
      method: 'GET',
      headers: userAuth(),
    });
  },

  async getMePetrolRole() {
    return fetchPetrolAPI('/admin/roles/user/me', {
      method: 'GET',
      headers: userAuth(),
    });
  },

  async getLocations(params) {
    return fetchAPI('/admin/locations', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getLocation(params) {
    return fetchAPI('/admin/locations/details', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  //kiosk api

  async getPartners(params) {
    return fetchPetrolAPI('/admin/partners', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },
  async getPartner(params) {
    return fetchPetrolAPI('/admin/partners/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },
  async createPartner(data) {
    return fetchPetrolAPI('/admin/partners', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async updatePartner(data) {
    return fetchPetrolAPI('/admin/partners/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async deletePartner(params) {
    return fetchPetrolAPI('/admin/partners/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },

  async getApiKeys(params) {
    return fetchPetrolAPI('/admin/api-keys', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async createApiKey(data) {
    return fetchPetrolAPI('/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },
  async updateApiKey(data) {
    return fetchPetrolAPI('/admin/api-keys/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async deleteApiKey(params) {
    return fetchPetrolAPI('/admin/api-keys/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },

  async getRequestLogs(params) {
    return fetchPetrolAPI('/admin/request-logs', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },
  async getRequestLog(params) {
    return fetchPetrolAPI('/admin/request-logs/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getPosTransactions(params) {
    return fetchPetrolAPI('/admin/pos-transactions', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  exportCSVPosTransactions(params) {
    return fetchPetrolAPI('/admin/pos-transactions/export', {
      method: 'GET',
      params,
      responseType: 'text',
      headers: userAuth(),
    });
  },

  async getPosTransaction(params) {
    return fetchPetrolAPI('/admin/pos-transactions/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getUserPurchasesStats(params) {
    return fetchPetrolAPI('/admin/user-purchases/stats', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getUserPurchases(params) {
    return fetchPetrolAPI('/admin/user-purchases', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async exportCSVUserPurchases(params) {
    return fetchPetrolAPI('/admin/user-purchases/export', {
      method: 'GET',
      params,
      responseType: 'text',
      headers: userAuth(),
    });
  },

  async getUserPurchase(params) {
    return fetchPetrolAPI('/admin/user-purchases/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getMarketPrices(params) {
    return fetchPetrolAPI('/admin/market-prices', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getMarketPrice(params) {
    return fetchPetrolAPI('/admin/market-prices/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async createMarketPrice(data) {
    return fetchPetrolAPI('/admin/market-prices', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async updateMarketPrice(data) {
    return fetchPetrolAPI('/admin/market-prices/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async deleteMarketPrice(params) {
    return fetchPetrolAPI('/admin/market-prices/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },

  async getBasePrices(params) {
    return fetchPetrolAPI('/admin/base-prices', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getBasePrice(params) {
    return fetchPetrolAPI('/admin/base-prices/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async createBasePrice(data) {
    return fetchPetrolAPI('/admin/base-prices', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async updateBasePrice(data) {
    return fetchPetrolAPI('/admin/base-prices/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async deleteBasePrice(params) {
    return fetchPetrolAPI('/admin/base-prices/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },

  async getNextBasePrices(params) {
    return fetchPetrolAPI('/admin/next-base-prices', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getNextBasePrice(params) {
    return fetchPetrolAPI('/admin/next-base-prices/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async createNextBasePrice(data) {
    return fetchPetrolAPI('/admin/next-base-prices', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async updateNextBasePrice(data) {
    return fetchPetrolAPI('/admin/next-base-prices/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async deleteNextBasePrice(params) {
    return fetchPetrolAPI('/admin/next-base-prices/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },

  async getStations(params) {
    return fetchPetrolAPI('/admin/stations', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStation(params) {
    return fetchPetrolAPI('/admin/stations/' + params.id, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async createStation(data) {
    return fetchPetrolAPI('/admin/stations', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async updateStation(data) {
    return fetchPetrolAPI('/admin/stations/' + data.id, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async deleteStation(params) {
    return fetchPetrolAPI('/admin/stations/' + params.id, {
      method: 'DELETE',
      headers: userAuth(),
    });
  },

  async getExternalPrices(params) {
    return fetchPetrolAPI('/admin/external-prices', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getSystemSettings(params) {
    return fetchPetrolAPI('/admin/system-settings', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async updateSystemSetting(data) {
    return fetchPetrolAPI('/admin/system-settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: userAuth(),
    });
  },

  async getStatsRevenue(params) {
    return fetchPetrolAPI('/admin/stats/revenue/' + params.range, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsPosTxCount(params) {
    return fetchPetrolAPI('/admin/stats/pos-txs/' + params.range, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsAvgCheck(params) {
    return fetchPetrolAPI('/admin/stats/avg-check/' + params.range, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsFuelLiters(params) {
    return fetchPetrolAPI('/admin/stats/fuel-liters/' + params.range, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsSales(params) {
    return fetchPetrolAPI('/admin/stats/sales/' + params.range, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsSummary(params) {
    return fetchPetrolAPI('/admin/stats/summary', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsFuelPrices(params) {
    return fetchPetrolAPI('/admin/stats/fuel-prices/summary', {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },

  async getStatsFuelPricesChart(params) {
    return fetchPetrolAPI('/admin/stats/fuel-prices/' + params.range, {
      method: 'GET',
      params,
      headers: userAuth(),
    });
  },
};
